import React, { useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createCustomer, updateCustomer } from '../services/api';
import { useCustomerStore, type Customer } from '../stores/customerStore';
import { useTaskStore } from '../stores/taskStore';
import { useOpportunityStore } from '../stores/opportunityStore';
import { Input, CustomSelect, LoadingButton, dialogButtonClass, DialogShell, DialogHeader, DialogFooter, FormFieldWithError } from '@redpanda/ui';
import { TagCloudSelector, getSourceOptions, normalizeSource } from './forms';
import { notifyDataChanged } from '../utils/dataEvents';
import { tagsByIds } from '../stores/tagStore';
import { toast } from 'react-hot-toast';

interface CustomerDialogProps {
  isOpen: boolean;
  /** 樂觀更新：送出後立即關閉（API 尚未完成）；需要重抓資料請用 useDataChanged('customers') */
  onClose: () => void;
  /** 有值時為編輯模式（客戶列表「編輯」、客戶詳情頁「編輯」共用） */
  customer?: Customer | null;
}

interface FormData {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  company?: string;
  source?: string;
  tags?: string[];
}

const toTagIds = (tags: Customer['tags'] | string[] | undefined): string[] =>
  (tags || []).map((tag: any) => (typeof tag === 'object' && tag ? tag.id : tag)).filter((id: any): id is string => typeof id === 'string' && !!id);

const errorMessage = (error: any, fallback: string) =>
  error.response?.data?.error && error.response.data.code !== 'VALIDATION_ERROR' ? error.response.data.error : fallback;

/**
 * 新增／編輯客戶（取代原本的新增對話框、快速編輯對話框、完整編輯頁三份表單）
 */
const CustomerDialog: React.FC<CustomerDialogProps> = ({ isOpen, onClose, customer }) => {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<FormData>();
  const { addCustomer, replaceCustomer, removeCustomer, updateCustomer: updateCustomerInStore } = useCustomerStore();
  const { tasks, updateTask } = useTaskStore();
  const { opportunities, updateOpportunity } = useOpportunityStore();
  const isEditMode = !!customer;


  // 開啟時帶入資料（編輯）或清空（新增）（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (!isOpen) return;
    reset({
      name: customer?.name || '',
      email: customer?.email || '',
      phone: customer?.phone || '',
      address: customer?.address || '',
      company: customer?.company || '',
      source: normalizeSource(customer?.source),
      tags: toTagIds(customer?.tags),
    });
  }, [isOpen, customer, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  // 編輯後，任務、商機列表上顯示的客戶名稱一起更新
  const syncRelatedCustomerInfo = (id: string, data: FormData) => {
    const info = { name: data.name, email: data.email, company: data.company };
    tasks.filter(task => task.customerId === id && task.customer).forEach(task => updateTask(task.id, { customer: { ...task.customer!, ...info } }));
    opportunities.filter(opp => opp.customerId === id && opp.customer).forEach(opp => updateOpportunity(opp.id, { customer: { ...opp.customer, ...info } }));
  };

  const onSubmit = async (data: FormData) => {
    const payload = { ...data, tags: data.tags || [] };
    handleClose();

    if (customer) {
      const original = { ...customer };
      updateCustomerInStore(customer.id, { ...payload, tags: tagsByIds(payload.tags) });
      syncRelatedCustomerInfo(customer.id, data);
      try {
        const response = await updateCustomer(customer.id, payload);
        updateCustomerInStore(customer.id, response.data);
        toast.success(t('common.messages.updateSuccess'));
        notifyDataChanged('customers');
      } catch (error: any) {
        console.error('客戶資料更新失敗:', error);
        updateCustomerInStore(customer.id, original);
        toast.error(errorMessage(error, t('common.messages.updateFailed')));
      }
      return;
    }

    // 新增：先放臨時項目，成功後換成真實資料
    const tempId = `temp-${Date.now()}`;
    const now = new Date().toISOString();
    addCustomer({ id: tempId, userId: '', ...payload, tags: [], createdAt: now, updatedAt: now });
    try {
      const response = await createCustomer(payload);
      replaceCustomer(tempId, response.data);
      toast.success(t('common.messages.createSuccess'));
      notifyDataChanged('customers');
    } catch (error: any) {
      console.error('客戶新增失敗:', error);
      removeCustomer(tempId);
      const limitReached = error.response?.data?.code === 'CUSTOMER_LIMIT_REACHED';
      toast.error(limitReached ? t('customers.list.limitReached', { ns: 'pages' }) : errorMessage(error, t('common.messages.createFailed')));
    }
  };

  if (!isOpen) return null;

  const maxLength = (length: number) => ({ value: length, message: t('common.forms.validation.maxLength', { length }) });
  const textField = (name: 'phone' | 'address' | 'company', label: string, length: number) => (
    <FormFieldWithError label={label} error={errors[name]?.message} htmlFor={name}>
      <Input type="text" id={name} {...register(name, { maxLength: maxLength(length) })} variant={errors[name] ? 'error' : 'default'} />
    </FormFieldWithError>
  );

  return (
    <DialogShell onClose={handleClose} className="max-w-2xl">
      <DialogHeader title={isEditMode ? t('customers.edit.title', { ns: 'pages' }) : t('customers.add.title', { ns: 'pages' })} onClose={handleClose} closeLabel={t('common.actions.close')} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-6">
          <FormFieldWithError label={t('common.forms.labels.name')} required={true} error={errors.name?.message} htmlFor="name">
            <Input
              type="text"
              id="name"
              {...register('name', { required: t('common.forms.validation.required'), maxLength: maxLength(100) })}
              variant={errors.name ? 'error' : 'default'}
            />
          </FormFieldWithError>

          <FormFieldWithError label={t('common.forms.labels.email')} error={errors.email?.message} htmlFor="email">
            <Input
              type="email"
              id="email"
              {...register('email', {
                pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: t('common.forms.validation.invalidEmail') },
              })}
              variant={errors.email ? 'error' : 'default'}
            />
          </FormFieldWithError>

          {textField('phone', t('common.forms.labels.phone'), 20)}
          {textField('address', t('common.forms.labels.address'), 200)}
          {textField('company', t('common.forms.labels.company'), 100)}

          <FormFieldWithError label={t('common.forms.labels.source')} htmlFor="source">
            <CustomSelect
              id="source"
              value={watch('source') || 'other'}
              onChange={(value) => setValue('source', value)}
              options={getSourceOptions(t)}
            />
          </FormFieldWithError>

          <FormFieldWithError label={t('common.forms.labels.tags')} htmlFor="tags">
            <TagCloudSelector
              value={watch('tags') || []}
              onChange={(value: string[]) => setValue('tags', value)}
            />
          </FormFieldWithError>
        </div>

        <DialogFooter>
          <button type="button" onClick={handleClose} className={dialogButtonClass.cancel}>
            {t('common.actions.cancel')}
          </button>
          <LoadingButton type="submit" isEditMode={isEditMode} t={t} />
        </DialogFooter>
      </form>
    </DialogShell>
  );
};

export default CustomerDialog;
